import { ArrowRightIcon, Spinner, Typography, useMediaQuery, CartIcon } from '@ag1/ui';
import { toPlainText } from '@portabletext/toolkit';
import { clickPurchaseIntentEvent } from '@portal/analytics/events/studio';
import { useSiteCart } from '@portal/shared/components/SiteCart/hooks/SiteCartContext';
import { useStoreValue } from '@portal/state/hooks/useStore';
import clsx from 'clsx';
import { LATEST_CHECKOUT_EXPERIENCE } from 'constants/sessionStorage';
import Link from 'next/link';
import { usePageContext } from 'processes/Page/PageContext';
import { useEffect, useMemo, useState } from 'react';
import { type StoreCode } from 'schema/storeCode';
import { useLocale } from 'shared/hooks/useLocale';
import { useShopifyCartUrl } from 'shared/hooks/useShopifyCartUrl';
import { CHECKOUT_TYPE } from 'state/cart/constants';
import styles from './ActionButton.module.scss';

export type ActionButtonType = {
  text: string;
  link: string;
  showArrowRight: boolean;
  hideIconSize?: 'l' | 'm' | 's' | '';
};

type ActionButtonProps = {
  button: ActionButtonType | undefined;
  marketingButton?: {
    topButton: any;
    marketingLink: string;
    showArrowRight: boolean;
    hideIconSize: string;
  };
};

const sendPurchaseIntentEvent = (store: StoreCode, buttonText: string, href: string) => {
  clickPurchaseIntentEvent({
    store,
    clickText: buttonText,
    location: 'header',
    url: href
  });
};

const useGetActionButtonCartData = (
  isAuthorized: boolean = false
): { url: string; onClick?: () => void } => {
  const { cart: cartData, openCart, cartCount } = useSiteCart();
  const latestCheckoutExperience =
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LATEST_CHECKOUT_EXPERIENCE.key);
  const locale = useLocale();
  const storeCode = useStoreValue();
  const { getShopifyCartUrl } = useShopifyCartUrl();
  const [enhancedUrl, setEnhancedUrl] = useState<string | null>(null);

  const hasBabItems = useMemo(
    () =>
      cartData &&
      !!cartData.lines?.length &&
      cartData.attributes?.some(
        (attr) => attr.key === CHECKOUT_TYPE.key && attr.value === CHECKOUT_TYPE.BAB
      ),
    [cartData]
  );

  // Pre-generate the enhanced URL with analytics parameters
  useEffect(() => {
    if (cartData?.id && cartData.checkoutUrl && hasBabItems) {
      const fetchEnhancedUrl = async () => {
        try {
          const url = await getShopifyCartUrl(storeCode, cartData.id, locale);
          setEnhancedUrl(url);
        } catch (error) {
          setEnhancedUrl(cartData.checkoutUrl);
        }
      };

      fetchEnhancedUrl();
    }
  }, [cartData, storeCode, locale, getShopifyCartUrl, hasBabItems]);

  // Cart scenario - empty cart
  if (!cartData || !cartCount) {
    return { url: '', onClick: () => openCart() };
  }

  // Cart scenario - user is logged out - don't open on BaB carts
  if (!isAuthorized && latestCheckoutExperience !== LATEST_CHECKOUT_EXPERIENCE.BAB) {
    return { url: '', onClick: () => openCart() };
  }

  if (hasBabItems && cartData.id && cartData.checkoutUrl) {
    // Use the enhanced URL if available, otherwise fall back to the checkoutUrl
    const url = enhancedUrl || cartData.checkoutUrl;

    // For BAB items, we just need to return the URL
    // The Link component will handle the navigation
    return { url };
  }

  // Default - open cart
  return { url: '', onClick: () => openCart() };
};

export const CartButton = ({ isAuthorized = false }: { isAuthorized?: boolean }) => {
  const { isCartLoading, cartCount } = useSiteCart();
  const { url, onClick } = useGetActionButtonCartData(isAuthorized);

  const innerContent = isCartLoading ? (
    <Spinner size={24} withOverlay={false} />
  ) : (
    <>
      {cartCount > 0 && (
        <div className={styles.cartCountBadge}>
          <Typography variant="caption" size="1" noPadding>
            {cartCount}
          </Typography>
        </div>
      )}
      <CartIcon className={styles.cartIcon} />
    </>
  );

  // If we have a URL and we're not in a loading state, use the Link component
  const content =
    url && !isCartLoading ? (
      <Link href={url} className={styles.cartButton}>
        {innerContent}
      </Link>
    ) : (
      <button
        type="button"
        className={clsx(styles.cartButton, { [styles.loading]: isCartLoading })}
        onClick={onClick}
        aria-label="Cart Button"
        disabled={isCartLoading}>
        {innerContent}
      </button>
    );

  return <div className={styles.cartButtonContainer}>{content}</div>;
};

export const ActionButton = ({ button: defaultButton, marketingButton }: ActionButtonProps) => {
  // During SSR and before hydration, render the fallback button
  // This ensures server and client render the same initial HTML
  const marketingButtonMapped = marketingButton
    ? {
        text: toPlainText(marketingButton.topButton),
        link: marketingButton.marketingLink,
        showArrowRight: marketingButton.showArrowRight,
        hideIconSize: marketingButton.hideIconSize
      }
    : undefined;

  const button = (marketingButtonMapped as ActionButtonType) || defaultButton;

  const store = useStoreValue();
  const shouldShowIcon = useMediaQuery(button?.hideIconSize || 0);
  const { shouldDisableCTALink, maybeReplaceOfferlessCheckoutLink } = usePageContext();

  const link = useMemo(() => {
    let result = button?.link || '';
    result = maybeReplaceOfferlessCheckoutLink(result) || '';
    return result;
  }, [button?.link, maybeReplaceOfferlessCheckoutLink]);

  const disableLink = useMemo(() => shouldDisableCTALink(link), [shouldDisableCTALink, link]);

  const children = useMemo(
    () => [
      button?.text,
      button?.showArrowRight && shouldShowIcon && (
        <ArrowRightIcon key={button?.text} className={styles.arrowRight} />
      )
    ],
    [button?.showArrowRight, button?.text, shouldShowIcon]
  );

  return disableLink ? (
    <button
      type="button"
      className={clsx(styles.button, styles.disabled)}
      aria-label={button?.text}
      disabled>
      {children}
    </button>
  ) : (
    <Link
      className={styles.button}
      href={link}
      aria-label={button?.text}
      onClick={() => sendPurchaseIntentEvent(store, button?.text || '', link)}>
      {children}
    </Link>
  );
};
